import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_RECORDING_SECONDS = 180;
const MIN_RECORDING_SECONDS = 2;

export type RecorderState = 'idle' | 'recording' | 'recorded';

export interface Recording {
  video: Blob;
  audio: Blob;
  url: string;
  seconds: number;
}

/**
 * Camera + mic for one answer at a time, as on the assessment page: the video recorder keeps
 * the picture for the recruiter, a separate audio-only recorder gives the service a small file
 * to transcribe. The camera stays on between answers; only the recorders are recreated.
 */
export const useAnswerRecorder = () => {
  const streamRef = useRef<MediaStream | null>(null);
  const videoRecorder = useRef<MediaRecorder | null>(null);
  const audioRecorder = useRef<MediaRecorder | null>(null);
  const videoChunks = useRef<Blob[]>([]);
  const audioChunks = useRef<Blob[]>([]);
  const startedAt = useRef<number>(0);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [recording, setRecording] = useState<Recording | null>(null);
  const [previewEl, setPreviewEl] = useState<HTMLVideoElement | null>(null);

  const startCamera = useCallback(async () => {
    if (streamRef.current) return true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setCameraReady(true);
      setCameraError(null);
      return true;
    } catch {
      setCameraError('We could not access your camera and microphone. Allow access in your browser, then try again.');
      return false;
    }
  }, []);

  // Attach the live stream to whichever preview element is mounted.
  useEffect(() => {
    if (previewEl && streamRef.current && state !== 'recorded') {
      previewEl.srcObject = streamRef.current;
      previewEl.muted = true;
      previewEl.play().catch(() => undefined);
    }
  }, [previewEl, cameraReady, state]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const stop = useCallback(() => {
    const v = videoRecorder.current;
    const a = audioRecorder.current;
    if (!v || !a || v.state === 'inactive') return;
    let pending = 2;
    const finish = () => {
      pending -= 1;
      if (pending > 0) return;
      const video = new Blob(videoChunks.current, { type: 'video/webm' });
      const audio = new Blob(audioChunks.current, { type: 'audio/webm' });
      const seconds = Math.round((Date.now() - startedAt.current) / 1000);
      setRecording({ video, audio, url: URL.createObjectURL(video), seconds });
      setState('recorded');
    };
    v.onstop = finish;
    a.onstop = finish;
    v.stop();
    a.stop();
  }, []);

  const start = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    if (recording) URL.revokeObjectURL(recording.url);
    setRecording(null);
    videoChunks.current = [];
    audioChunks.current = [];
    const v = new MediaRecorder(stream, { mimeType: 'video/webm' });
    const a = new MediaRecorder(new MediaStream(stream.getAudioTracks()), { mimeType: 'audio/webm' });
    v.ondataavailable = (e) => { if (e.data.size) videoChunks.current.push(e.data); };
    a.ondataavailable = (e) => { if (e.data.size) audioChunks.current.push(e.data); };
    videoRecorder.current = v;
    audioRecorder.current = a;
    v.start(1000);
    a.start(1000);
    startedAt.current = Date.now();
    setElapsed(0);
    setState('recording');
  }, [recording]);

  // Tick the timer, and stop at the limit.
  useEffect(() => {
    if (state !== 'recording') return undefined;
    const timer = setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAt.current) / 1000);
      setElapsed(seconds);
      if (seconds >= MAX_RECORDING_SECONDS) stop();
    }, 250);
    return () => clearInterval(timer);
  }, [state, stop]);

  const reset = useCallback(() => {
    if (recording) URL.revokeObjectURL(recording.url);
    setRecording(null);
    setElapsed(0);
    setState('idle');
  }, [recording]);

  return {
    startCamera,
    cameraReady,
    cameraError,
    previewRef: setPreviewEl,
    state,
    elapsed,
    recording,
    canStop: state === 'recording' && elapsed >= MIN_RECORDING_SECONDS,
    start,
    stop,
    reset,
  };
};
